import { useState, useEffect, useCallback } from "react";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const useOrderAnalytics = ({
  dateFrom,
  dateTo,
  platformName,
  status,
  autoLoad = true
} = {}) => {
  const [metrics, setMetrics] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [trends, setTrends] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Carregar métricas completas
  const loadMetrics = useCallback(async (customFilters = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        dateFrom,
        dateTo,
        platformName,
        status,
        ...customFilters
      };

      // Remover parâmetros undefined
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      const { data } = await api.get('/orders/analytics/metrics', { params });
      setMetrics(data.data);
      return data.data;
    } catch (err) {
      setError(err);
      toastError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, platformName, status]);

  // Carregar comparação de plataformas
  const loadPlatforms = useCallback(async (customFilters = {}) => {
    try {
      const params = {
        dateFrom,
        dateTo,
        ...customFilters
      };

      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      const { data } = await api.get('/orders/analytics/platforms', { params });
      setPlatforms(data.data);
      return data;
    } catch (err) {
      toastError(err);
      throw err;
    }
  }, [dateFrom, dateTo]);

  // Carregar tendências temporais
  const loadTrends = useCallback(async (period = 'day', customFilters = {}) => {
    try {
      const params = {
        dateFrom,
        dateTo,
        period,
        platformName,
        status,
        ...customFilters
      };

      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      const { data } = await api.get('/orders/analytics/trends', { params });
      setTrends(data.data);
      return data;
    } catch (err) {
      toastError(err);
      throw err;
    }
  }, [dateFrom, dateTo, platformName, status]);

  // Carregar KPIs executivos
  const loadKPIs = useCallback(async (customFilters = {}) => {
    try {
      const params = {
        dateFrom,
        dateTo,
        ...customFilters
      };

      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      const { data } = await api.get('/orders/analytics/kpis', { params });
      setKpis(data.data);
      return data.data;
    } catch (err) {
      toastError(err);
      throw err;
    }
  }, [dateFrom, dateTo]);

  // Carregar todos os dados
  const loadAll = useCallback(async (customFilters = {}) => {
    try {
      setLoading(true);
      setError(null);

      await Promise.all([
        loadMetrics(customFilters),
        loadPlatforms(customFilters),
        loadTrends('day', customFilters),
        loadKPIs(customFilters)
      ]);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [loadMetrics, loadPlatforms, loadTrends, loadKPIs]);

  // Exportar dados
  const exportData = useCallback(async (type = 'full', format = 'json') => {
    try {
      const params = {
        dateFrom,
        dateTo,
        type,
        format
      };

      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      const response = await api.get('/orders/analytics/export', { 
        params,
        responseType: format === 'csv' ? 'blob' : 'json'
      });

      if (format === 'csv') {
        // Criar download do CSV
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `analytics-${type}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      return response.data;
    } catch (err) {
      toastError(err);
      throw err;
    }
  }, [dateFrom, dateTo]);

  // Auto-carregar dados quando filtros mudarem
  useEffect(() => {
    if (autoLoad && (dateFrom && dateTo)) {
      loadAll();
    }
  }, [dateFrom, dateTo, platformName, status, autoLoad, loadAll]);

  // Funções de conveniência para componentes
  const getMetricGrowth = useCallback((current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  }, []);

  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  }, []);

  const formatPercent = useCallback((value, decimals = 1) => {
    return `${(value || 0).toFixed(decimals)}%`;
  }, []);

  return {
    // Dados
    metrics,
    platforms,
    trends,
    kpis,
    
    // Estados
    loading,
    error,
    
    // Funções de carregamento
    loadMetrics,
    loadPlatforms,
    loadTrends,
    loadKPIs,
    loadAll,
    exportData,
    
    // Utilidades
    getMetricGrowth,
    formatCurrency,
    formatPercent
  };
};

export default useOrderAnalytics;